import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ZeosApiContext } from '../../context/ZeosApiContext';
import { AuthContext } from '../../context/AuthContext';
import { FeatureFlags } from '../../configs/FeatureFlags';
import {
  ICompliancePortfolioSummary,
  ICompliancePropertyStatus,
  ComplianceStatus,
} from '../../services/zeos-api/contracts/Compliance';
import { ICompanyContract } from '../../services/zeos-api/contracts/Company';
import { pageConfigBySlug } from '../../services/nav/NavigationService';
import ComplianceImage from '../../assets/mock/2025-Rewire-ZEOS-ComplianceGridv3.pdf.png';
import { DashboardFooter } from './layout/DashboardFooter';
import { DashboardHeader } from './layout/DashboardHeader';
import { ZeosSpinner } from '../../components/Shared/ZeosSpinner';

const statusOrder: ComplianceStatus[] = [
  'ActionNeeded',
  'AtRisk',
  'Unknown',
  'Compliant',
  'ComingSoon',
];

const statusLabel: Record<ComplianceStatus, string> = {
  ActionNeeded: 'Action Needed',
  AtRisk: 'At Risk',
  Unknown: 'Unknown',
  Compliant: 'Compliant',
  ComingSoon: 'Coming soon',
};

const statusTone: Record<ComplianceStatus, string> = {
  ActionNeeded: 'bg-red-50 text-red-800 border-red-200',
  AtRisk: 'bg-amber-50 text-amber-800 border-amber-200',
  Unknown: 'bg-gray-50 text-gray-800 border-gray-200',
  Compliant: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  // Neutral on purpose: an interim placeholder, not a warning or a data gap (task-4051).
  ComingSoon: 'bg-sky-50 text-sky-800 border-sky-200',
};

// Interim until the Automated Compliance feature (task-4051, Al 2026-10-12).
const INTERIM_HEADING = 'Regulatory status: coming soon';
const INTERIM_BODY =
  'Automated compliance checks are on the way. Until then, ZEOS shows how complete each property\'s meter data is.';

const portfolioCardStyles = {
  regulated: {
    card: 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-100/60',
    label: 'text-sky-700',
    value: 'text-sky-900',
    badge: 'bg-sky-100 text-sky-700 border-sky-200',
  },
  atRisk: {
    card: 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-100/70',
    label: 'text-amber-700',
    value: 'text-amber-900',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
  },
  actionNeeded: {
    card: 'border-red-200 bg-gradient-to-br from-red-50 via-white to-red-100/70',
    label: 'text-red-700',
    value: 'text-red-900',
    badge: 'bg-red-100 text-red-800 border-red-200',
  },
  unknown: {
    card: 'border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-100/80',
    label: 'text-slate-700',
    value: 'text-slate-900',
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
  },
} as const;

type PortfolioCardFilter = 'regulated' | ComplianceStatus;

const reportFormats = [
  { value: 'pdf', label: 'PDF' },
  { value: 'csv', label: 'CSV' },
  { value: 'docx', label: 'Word' },
];

export const ComplianceScreen = () => {
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState<ICompliancePortfolioSummary | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<ICompliancePropertyStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [reportFormat, setReportFormat] = useState('pdf');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<PortfolioCardFilter | null>(null);
  const [hoveredCard, setHoveredCard] = useState<PortfolioCardFilter | null>(null);
  // Coverage qualifiers come from the property endpoint only (not the portfolio summary), task-4051.
  const [qualifiers, setQualifiers] = useState<string[]>([]);

  const selectedPropertyId = selectedProperty?.propertyId;
  useEffect(() => {
    let cancelled = false;
    setQualifiers([]);
    if (!selectedPropertyId) {
      return;
    }
    const loadQualifiers = async () => {
      try {
        const response = await api.Compliance.getPropertyCompliance(selectedPropertyId);
        if (!cancelled && response?.zeosStatus?.toLowerCase() === 'success') {
          setQualifiers(response.data?.coverageQualifiers ?? []);
        }
      } catch {
        // A disclosure only: the detail panel still shows without it.
      }
    };
    loadQualifiers();
    return () => {
      cancelled = true;
    };
  }, [api.Compliance, selectedPropertyId]);

  useEffect(() => {
    const resolveCompany = async () => {
      const user = await auth.zeosUserAsync();
      const claimCompany = user?.customerClaims?.[0]?.companies?.[0]?.companyId;
      if (claimCompany) {
        setCompanyId(claimCompany);
        return;
      }

      const customerId = user?.customerClaims?.[0]?.customerId;
      if (!customerId) {
        setCompanyId(null);
        return;
      }

      const response = await api.Company.companiesByCustIdAsync(customerId);
      const companies = response?.data as ICompanyContract[] | undefined;
      setCompanyId(companies?.[0]?.companyId ?? null);
    };

    resolveCompany();
  }, [api.Company, auth]);

  useEffect(() => {
    if (!FeatureFlags.enableCompliancePortfolioDashboard) {
      return;
    }

    if (!companyId) {
      return;
    }

    const loadPortfolio = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        const response = await api.Compliance.getPortfolioSummary(companyId);
        if (response?.zeosStatus?.toLowerCase() !== 'success') {
          throw new Error(response?.message || 'Failed to load compliance summary.');
        }
        setPortfolio(response.data ?? null);
        if (response.data?.properties?.length) {
          setSelectedProperty(response.data.properties[0]);
        }
      } catch (error: any) {
        setErrorMessage(error?.message || 'Failed to load compliance data.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPortfolio();
  }, [api.Compliance, companyId]);

  const sortedProperties = useMemo(() => {
    if (!portfolio?.properties) return [];
    return [...portfolio.properties].sort((a, b) => {
      const statusA = statusOrder.indexOf(a.status);
      const statusB = statusOrder.indexOf(b.status);
      if (statusA !== statusB) return statusA - statusB;
      return a.propertyId - b.propertyId;
    });
  }, [portfolio]);

  const resolvePropertyLabel = (property: ICompliancePropertyStatus) => {
    const name = property.propertyName?.trim();
    return name ? name : `Property ${property.propertyId}`;
  };

  const propertyMatchesCard = (
    property: ICompliancePropertyStatus,
    card: PortfolioCardFilter | null
  ) => {
    if (!card) {
      return false;
    }

    if (card === 'regulated') {
      return (property.regulations?.length ?? 0) > 0;
    }

    return property.status === card;
  };

  const engagedCard = hoveredCard ?? activeCard;

  const buildMitigationSteps = (property: ICompliancePropertyStatus) => {
    const missingFields = property.missingData?.map((item) => item.field).filter(Boolean) ?? [];
    const nextDeadline = property.nextDeadline?.date
      ? `${property.nextDeadline.label ?? 'deadline'} by ${property.nextDeadline.date}`
      : null;

    if (property.status === 'ComingSoon') {
      return 'Regulatory checks are coming soon.';
    }

    if (property.status === 'ActionNeeded') {
      if (missingFields.length > 0) {
        return `Resolve missing inputs: ${missingFields.join(', ')}.`;
      }

      if (nextDeadline) {
        return `Complete filing and remediation for ${nextDeadline}.`;
      }

      return 'Review the triggered regulation and complete remediation immediately.';
    }

    if (property.status === 'AtRisk') {
      if (missingFields.length > 0 && nextDeadline) {
        return `Confirm ${missingFields.join(', ')} and prepare for ${nextDeadline}.`;
      }

      if (nextDeadline) {
        return `Prepare documentation and complete filing for ${nextDeadline}.`;
      }

      if (missingFields.length > 0) {
        return `Confirm ${missingFields.join(', ')} before the next filing cycle.`;
      }

      return 'Review thresholds now and prepare the next required filing.';
    }

    if (property.status === 'Unknown') {
      if (missingFields.length > 0) {
        return `Add the missing data needed to evaluate compliance: ${missingFields.join(', ')}.`;
      }

      return 'Review the property profile and add enough data to determine compliance status.';
    }

    if (nextDeadline) {
      return `Maintain records and monitor ${nextDeadline}.`;
    }

    return 'Keep operating data current and monitor for the next compliance trigger.';
  };

  const hoveredRowTone = (card: PortfolioCardFilter | null) => {
    switch (card) {
      case 'regulated':
        return 'bg-sky-50/80 ring-1 ring-inset ring-sky-200';
      case 'AtRisk':
        return 'bg-amber-50/80 ring-1 ring-inset ring-amber-200';
      case 'ActionNeeded':
        return 'bg-red-50/80 ring-1 ring-inset ring-red-200';
      case 'Unknown':
        return 'bg-slate-50/90 ring-1 ring-inset ring-slate-200';
      case 'Compliant':
        return 'bg-emerald-50/80 ring-1 ring-inset ring-emerald-200';
      default:
        return '';
    }
  };

  const handleNavigateToProperty = (property: ICompliancePropertyStatus) => {
    const targetCompanyId = property.companyId ?? companyId;
    if (!targetCompanyId) {
      return;
    }
    navigate(
      `/${pageConfigBySlug('dashboard')?.slug || 'dashboard'}?company=${targetCompanyId}&property=${property.propertyId}`
    );
  };

  const handleDownload = async (scope: 'portfolio' | 'property') => {
    if (!companyId) {
      setDownloadStatus('Company is required for reporting.');
      return;
    }
    if (scope === 'property' && !selectedProperty?.propertyId) {
      setDownloadStatus('Select a property before downloading.');
      return;
    }

    try {
      setIsDownloading(true);
      setDownloadStatus('Preparing download...');
      const response = scope === 'portfolio'
        ? await api.Compliance.downloadPortfolioReport(companyId, reportFormat)
        : await api.Compliance.downloadPropertyReport(selectedProperty!.propertyId, reportFormat);

      if (response?.zeosStatus && response.zeosStatus.toLowerCase() !== 'success') {
        throw new Error('Download failed.');
      }

      const raw = response.rawResponse;
      if (!raw) {
        throw new Error('Download failed.');
      }

      const contentType = (raw.headers?.['content-type'] as string) || 'application/octet-stream';
      const blob = new Blob([raw.data], { type: contentType });
      const fileName = resolveFileName(
        raw.headers?.['content-disposition'],
        `${scope}-compliance.${reportFormat}`
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadStatus('Download started.');
    } catch (error: any) {
      setDownloadStatus(error?.message || 'Download failed.');
    } finally {
      setIsDownloading(false);
    }
  };

  const resolveFileName = (contentDisposition?: string, fallback?: string) => {
    if (!contentDisposition) return fallback || 'compliance-report';
    const match = /filename="?([^";]+)"?/i.exec(contentDisposition);
    return match?.[1] || fallback || 'compliance-report';
  };

  if (!FeatureFlags.enableCompliancePortfolioDashboard) {
    return (
      <div className="w-screen min-h-screen bg-zeos-white-md">
        <DashboardHeader />
        <img
          src={ComplianceImage}
          alt="ZEOS Compliance Grid Placeholder"
          className="w-screen h-auto block"
        />
        <DashboardFooter />
      </div>
    );
  }

  return (
    <div className="w-screen min-h-screen bg-zeos-white-md">
      <DashboardHeader />
      <div className="w-full px-6 py-8 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Compliance Portfolio</h1>
            <p className="text-sm text-gray-600">
              {portfolio?.isInterim
                ? INTERIM_HEADING
                : 'NYC, NY State, NH, MA, and CA regulatory status overview.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="border border-gray-300 rounded px-3 py-2 text-sm"
              value={reportFormat}
              onChange={(event) => setReportFormat(event.target.value)}
            >
              {reportFormats.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => handleDownload('portfolio')}
              disabled={isDownloading}
              className="px-4 py-2 text-sm rounded bg-zeos-orange-dk text-white disabled:opacity-50"
            >
              {isDownloading ? 'Downloading...' : 'Download Portfolio Report'}
            </button>
            <button
              type="button"
              onClick={() => handleDownload('property')}
              disabled={isDownloading || !selectedProperty}
              className="px-4 py-2 text-sm rounded border border-zeos-orange-dk text-zeos-orange-dk disabled:opacity-50"
            >
              {isDownloading ? 'Downloading...' : 'Download Property Report'}
            </button>
          </div>
        </div>

        {downloadStatus && (
          <div className="text-xs text-gray-700 border border-gray-200 bg-white rounded p-2">
            {downloadStatus}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <ZeosSpinner />
          </div>
        ) : errorMessage ? (
          <div className="border border-red-200 bg-red-50 text-red-700 rounded p-4">
            {errorMessage}
          </div>
        ) : portfolio ? (
          <>
            {portfolio.isInterim ? (
              // The status counts would all read zero during the interim, so one notice replaces them.
              <div role="status" aria-label="Interim compliance notice" className="rounded-xl border border-sky-200 bg-sky-50 p-4 shadow-sm">
                <div className="text-sm font-semibold text-sky-900">{INTERIM_HEADING}</div>
                <div className="mt-1 text-xs text-sky-800">{INTERIM_BODY}</div>
              </div>
            ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button
                type="button"
                className={`rounded-xl border p-4 shadow-sm text-left transition-all ${portfolioCardStyles.regulated.card} ${
                  engagedCard === 'regulated' ? 'ring-2 ring-sky-300 shadow-md' : ''
                }`}
                onMouseEnter={() => setHoveredCard('regulated')}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setActiveCard((current) => (current === 'regulated' ? null : 'regulated'))}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${portfolioCardStyles.regulated.label}`}>
                      Regulated Buildings
                    </div>
                    <div className={`mt-3 text-3xl font-semibold ${portfolioCardStyles.regulated.value}`}>
                      {portfolio.regulatedCount}
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-medium ${portfolioCardStyles.regulated.badge}`}>
                    In scope
                  </span>
                </div>
                <div className="mt-3 text-xs text-sky-800">
                  Buildings currently covered by tracked regulations.
                </div>
              </button>
              <button
                type="button"
                className={`rounded-xl border p-4 shadow-sm text-left transition-all ${portfolioCardStyles.atRisk.card} ${
                  engagedCard === 'AtRisk' ? 'ring-2 ring-amber-300 shadow-md' : ''
                }`}
                onMouseEnter={() => setHoveredCard('AtRisk')}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setActiveCard((current) => (current === 'AtRisk' ? null : 'AtRisk'))}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${portfolioCardStyles.atRisk.label}`}>
                      At Risk
                    </div>
                    <div className={`mt-3 text-3xl font-semibold ${portfolioCardStyles.atRisk.value}`}>
                      {portfolio.atRiskCount}
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-medium ${portfolioCardStyles.atRisk.badge}`}>
                    Monitor
                  </span>
                </div>
                <div className="mt-3 text-xs text-amber-800">
                  Properties nearing a compliance threshold or deadline.
                </div>
              </button>
              <button
                type="button"
                className={`rounded-xl border p-4 shadow-sm text-left transition-all ${portfolioCardStyles.actionNeeded.card} ${
                  engagedCard === 'ActionNeeded' ? 'ring-2 ring-red-300 shadow-md' : ''
                }`}
                onMouseEnter={() => setHoveredCard('ActionNeeded')}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setActiveCard((current) => (current === 'ActionNeeded' ? null : 'ActionNeeded'))}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${portfolioCardStyles.actionNeeded.label}`}>
                      Action Needed
                    </div>
                    <div className={`mt-3 text-3xl font-semibold ${portfolioCardStyles.actionNeeded.value}`}>
                      {portfolio.actionNeededCount}
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-medium ${portfolioCardStyles.actionNeeded.badge}`}>
                    Review now
                  </span>
                </div>
                <div className="mt-3 text-xs text-red-800">
                  Missing filings, data, or remediation steps blocking compliance.
                </div>
              </button>
              <button
                type="button"
                className={`rounded-xl border p-4 shadow-sm text-left transition-all ${portfolioCardStyles.unknown.card} ${
                  engagedCard === 'Unknown' ? 'ring-2 ring-slate-300 shadow-md' : ''
                }`}
                onMouseEnter={() => setHoveredCard('Unknown')}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setActiveCard((current) => (current === 'Unknown' ? null : 'Unknown'))}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${portfolioCardStyles.unknown.label}`}>
                      Unknown
                    </div>
                    <div className={`mt-3 text-3xl font-semibold ${portfolioCardStyles.unknown.value}`}>
                      {portfolio.unknownCount}
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-medium ${portfolioCardStyles.unknown.badge}`}>
                    Data gap
                  </span>
                </div>
                <div className="mt-3 text-xs text-slate-700">
                  Properties still missing enough data to assign a clear status.
                </div>
              </button>
            </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-gray-200 rounded">
                <div className="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-700">
                  Portfolio Status
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-2">Property</th>
                        <th className="px-4 py-2">Sq Ft</th>
                        <th className="px-4 py-2">Status</th>
                        <th className="px-4 py-2">Next Deadline</th>
                        <th className="px-4 py-2">Mitigation Steps</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedProperties.map((property) => {
                        const isSelected = selectedProperty?.propertyId === property.propertyId;
                        const isHoveredMatch = propertyMatchesCard(property, engagedCard);
                        const shouldFade = Boolean(engagedCard) && !isHoveredMatch;

                        return (
                          <tr
                            key={property.propertyId}
                            className={`border-t border-gray-100 cursor-pointer transition-all ${
                              isHoveredMatch
                                ? hoveredRowTone(engagedCard)
                                : isSelected
                                  ? 'bg-gray-50'
                                  : 'hover:bg-gray-50'
                            } ${shouldFade ? 'opacity-45' : ''}`}
                            onClick={() => setSelectedProperty(property)}
                          >
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              className="text-left text-zeos-orange-dk hover:underline"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleNavigateToProperty(property);
                              }}
                            >
                              {resolvePropertyLabel(property)}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            {property.sqFt ? property.sqFt.toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-1 text-xs border rounded ${statusTone[property.status]}`}
                            >
                              {statusLabel[property.status]}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {property.nextDeadline?.date
                              ? `${property.nextDeadline.label ?? 'Deadline'} (${property.nextDeadline.date})`
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            {buildMitigationSteps(property)}
                          </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded p-4 space-y-4">
                <div>
                  <div className="text-sm font-semibold text-gray-700">Property Detail</div>
                  <div className="text-xs text-gray-500">
                    {selectedProperty ? resolvePropertyLabel(selectedProperty) : 'Select a property'}
                  </div>
                  {selectedProperty && (
                    <div className="text-xs text-gray-500">
                      {[selectedProperty.city, selectedProperty.state, selectedProperty.zip]
                        .filter(Boolean)
                        .join(', ') || 'Location unavailable'}
                    </div>
                  )}
                  {selectedProperty && (
                    <div className="text-xs text-gray-500">
                      Sq Ft: {selectedProperty.sqFt ? selectedProperty.sqFt.toLocaleString() : '—'}
                    </div>
                  )}
                </div>
                {selectedProperty ? (
                  <>
                    <div className="space-y-2">
                      <div className={`inline-flex items-center px-2 py-1 text-xs border rounded ${statusTone[selectedProperty.status]}`}>
                        {statusLabel[selectedProperty.status]}
                      </div>
                      <div className="text-xs text-gray-600">
                        {selectedProperty.statusReason || 'No status reason available.'}
                      </div>
                      {/* Meter data coverage, one line per service; never changes the status (task-4051). */}
                      {qualifiers.length > 0 && (
                        <ul aria-label="Meter data coverage" className="space-y-0.5 text-xs italic text-gray-600">
                          {qualifiers.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      )}
                      {selectedProperty.nextDeadline?.date && (
                        <div className="text-xs text-gray-700">
                          Next deadline: {selectedProperty.nextDeadline.label} ({selectedProperty.nextDeadline.date})
                        </div>
                      )}
                    </div>

                    {/* The placeholder regulations and their data checks are hidden during the interim. */}
                    {!selectedProperty.isInterim && (
                    <>
                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-2">Missing Data</div>
                      {selectedProperty.missingData && selectedProperty.missingData.length > 0 ? (
                        <ul className="space-y-1 text-xs text-gray-600">
                          {selectedProperty.missingData.map((item, index) => (
                            <li key={`${item.field}-${index}`}>{item.field}: {item.message}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="text-xs text-gray-600">None</div>
                      )}
                    </div>

                    <div>
                      <div className="text-xs font-semibold text-gray-700 mb-2">Regulations</div>
                      {selectedProperty.regulations && selectedProperty.regulations.length > 0 ? (
                        <div className="space-y-2">
                          {selectedProperty.regulations.map((regulation) => (
                            <div key={regulation.regulationId} className="border border-gray-200 rounded p-2">
                              <div className="text-xs font-semibold text-gray-800">
                                {regulation.regulationName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {regulation.jurisdictionState}
                                {regulation.jurisdictionCity ? ` · ${regulation.jurisdictionCity}` : ''}
                              </div>
                              <div className={`inline-flex items-center px-2 py-1 text-[10px] border rounded mt-2 ${statusTone[regulation.status]}`}>
                                {statusLabel[regulation.status]}
                              </div>
                              {regulation.nextDeadline?.date && (
                                <div className="text-[10px] text-gray-600 mt-1">
                                  Deadline: {regulation.nextDeadline.label} ({regulation.nextDeadline.date})
                                </div>
                              )}
                              {regulation.missingData && regulation.missingData.length > 0 && (
                                <div className="text-[10px] text-gray-600 mt-1">
                                  Missing: {regulation.missingData.map((item) => item.field).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-600">No regulations matched.</div>
                      )}
                    </div>
                    </>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-gray-500">Select a property to view detail.</div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="border border-gray-200 bg-white rounded p-4">No compliance data available.</div>
        )}
      </div>
      <DashboardFooter />
    </div>
  );
};
